import React from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import iconScreenshot from '../../../../assets/img_icon_image.jpg';
import logoScreenshot from '../../../../assets/img_logo_image.jpg';
import splashScreenshot from '../../../../assets/img_splash_image.jpg';
import loginBackgroundScreenshot from '../../../../assets/img_login_background_image.jpg';
import rewardIconScreenshot from '../../../../assets/img_reward_image.jpg';
import emptyBagIconScreenshot from '../../../../assets/img_empty_bag_image.jpg';
import fullBagIconScreenshot from '../../../../assets/img_full_bag_image.jpg';
import rewardNavigationIconScreenshot from '../../../../assets/img_reward_navigation_image.jpg';
import loginLogoScreenshot from '../../../../assets/img_login_logo_image.jpg';
import ImageUpload, { type IImageUploadInstance } from './ImageUpload';
import useTenantStore, { selectTenantId } from '../../../../stores/TenantStore';
import OpenAPI from '../../../../endpoint/Openapi';
import MyAccordion from './MyAccordion';
import { type components } from '../../../../endpoint/Schema';
import LoginBackgroundImageUpload from './LoginBackgroundImageUpload';
import { type CustomError } from '../../../../models/Error';

interface IImage {
  title: string;
  appAssetsType: components['schemas']['AppAssetsType'];
  description: string;
}

const IMAGES: IImage[] = [
  {
    title: 'Icon',
    appAssetsType: 'Icon1024',
    description: 'If a icon is not provided, the Driverly icon will be used.',
  },
  {
    title: 'Logo',
    appAssetsType: 'Logo',
    description: 'If a logo is not provided, the Driverly logo will be used.',
  },
  {
    title: 'Splash screen',
    appAssetsType: 'SplashScreenXxxHdpi',
    description:
      'If a splash screen image is not provided, the Driverly splash screen image will be used',
  },
  {
    title: 'Login background',
    appAssetsType: 'LoginBackground',
    description:
      'If a login background image is not provided, the Driverly login background image will be used.',
  },
  {
    title: 'Reward',
    appAssetsType: 'RewardIcon',
    description:
      'If a reward icon is not provided, the Driverly reward icon image will be used.',
  },
  {
    title: 'Reward navigation',
    appAssetsType: 'RewardNavigationIcon',
    description:
      'If a reward navigation icon is not provided, the Driverly reward navigation icon image will be used.',
  },
  {
    title: 'Empty bag',
    appAssetsType: 'EmptyBagIcon',
    description:
      'If a empty bag icon is not provided, the Driverly empty bag icon image will be used.',
  },
  {
    title: 'Full bag',
    appAssetsType: 'FullBagIcon',
    description:
      'If a full bag icon is not provided, the Driverly full bag icon image will be used.',
  },
  {
    title: 'Login logo',
    appAssetsType: 'BwLogo',
    description:
      'If a login logo is not provided, the Driverly login logo will be used.',
  },
  {
    title: 'Tracking asset',
    appAssetsType: 'Tracking512' as components['schemas']['AppAssetsType'],
    description:
      'Upload a transparent PNG with white content only. This will be used for vehicle tracking visualization. Automatically generates sizes: 40px, 128px, 193px, 256px, 384px, 512px. If not provided, the default tracking asset will be used.',
  },
];

function Images() {
  const { setTenantConfig } = useTenantStore();
  const tenantId = useTenantStore(selectTenantId);
  const [expandedIndex, setExpandedIndex] = React.useState<number>();
  const [screenshot, setScreenshot] = React.useState(iconScreenshot);
  const [pending, setPending] = React.useState(false);
  const toast = React.useRef<Toast>(null);
  const imagesRef = React.useRef<Array<IImageUploadInstance | null>>([]);

  const handleUpload = async (
    imageUpload: IImageUploadInstance | null,
    title: string,
  ) => {
    try {
      if (imageUpload?.isFileSelected()) {
        const res = await imageUpload?.save();

        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        if (res.error) throw res.error;

        toast.current?.show({
          severity: 'info',
          summary: 'Success',
          detail: `${title} uploaded`,
        });
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }
  };

  const handleSave = async () => {
    setPending(true);

    await Promise.all(
      IMAGES.map(async (img, index) => {
        if (imagesRef.current[index]) {
          await handleUpload(imagesRef.current[index], img.title);
        }
      }),
    );

    try {
      const { data } = await OpenAPI.GET('/api/v2/admin/tenant', {
        params: {
          query: {
            tenantId,
          },
        },
      });
      if (data) setTenantConfig(data);
    } catch {}

    setPending(false);
  };

  function onToggle(index: number) {
    setExpandedIndex((prev) => (index === prev ? undefined : index));

    switch (index) {
      case 0:
        setScreenshot(iconScreenshot);
        break;
      case 1:
        setScreenshot(logoScreenshot);
        break;
      case 2:
        setScreenshot(splashScreenshot);
        break;
      case 3:
        setScreenshot(loginBackgroundScreenshot);
        break;
      case 4:
        setScreenshot(rewardIconScreenshot);
        break;
      case 5:
        setScreenshot(rewardNavigationIconScreenshot);
        break;
      case 6:
        setScreenshot(emptyBagIconScreenshot);
        break;
      case 7:
        setScreenshot(fullBagIconScreenshot);
        break;
      case 8:
        setScreenshot(loginLogoScreenshot);
        break;
      case 9:
        setScreenshot(iconScreenshot); // Using icon screenshot as placeholder for tracking
        break;
    }
  }

  function renderItem(img: IImage, index: number) {
    return (
      <div key={`img_${img.appAssetsType}_${index}`} className="max-w-md">
        <MyAccordion
          index={index}
          expandedIndex={expandedIndex}
          onToggle={onToggle}
          title={img.title}
          appAssetsType={img.appAssetsType}
        >
          <div className="flex flex-row">
            {img.appAssetsType === 'LoginBackground' ? (
              <LoginBackgroundImageUpload
                ref={(el) => (imagesRef.current[index] = el)}
              />
            ) : (
              <ImageUpload
                ref={(el) => (imagesRef.current[index] = el)}
                appAssetsType={img.appAssetsType}
              />
            )}
          </div>

          <div className="mt-5 leading-7 text-zinc-500">{img.description}</div>
        </MyAccordion>
      </div>
    );
  }

  return (
    <div>
      <div className="gap-4">
        <div className="flex flex-row w-full items-start gap-4 max-md:flex-col max-md:gap-8 max-md:items-center">
          <div className="flex flex-col mr-4 w-[50%] max-xl:w-[90%] max-w-[512px] bg-zinc-700 rounded-3xl max-md:mr-0 max-md:w-full max-md:max-w-[320px]">
            <img src={screenshot} />
          </div>

          <div className="flex flex-col justify-center w-full px-6 py-8 rounded-3xl shadow-[0px_4px_12px_rgba(0,0,0,0.1)] text-base leading-6 text-black bg-white">
            <div className="text-3xl font-medium mb-6">Images</div>
            <div className=" mb-4">Upload images by opening the accordions</div>

            <div className="flex flex-row flex-wrap w-full gap-x-4 gap-y-8">
              {IMAGES.map(renderItem)}
            </div>
          </div>
        </div>

        <div className="flex flex-row w-full px-6 py-6 justify-end">
          <Button
            label="Save"
            className={`text-2xl px-14 py-3 bg-secondary hover:bg-bluegray-400 text-black border-none`}
            onClick={handleSave}
            loading={pending}
          />
        </div>
      </div>

      <Toast ref={toast} className="mt-header-height" />
    </div>
  );
}

export default Images;
